import React from 'react';
import { Helmet } from 'react-helmet-async';
import { blogPosts } from '@/components/blog/blogData';
import BlogListing from '@/components/blog/BlogListing';
import BlogDetailView from '@/components/blog/BlogDetailView';
import { useBlogState } from '@/hooks/useBlogState';
import SEOMetadata from '@/components/common/SEOMetadata';

const Blog: React.FC = () => {
  
  const {
    selectedPost,
    isLoading,
    searchQuery,
    filteredPosts,
    safeBlogPosts,
    handlePostClick,
    handleBackClick,
    handleSearchChange,
    handleClearSearch,
  } = useBlogState(blogPosts);

  // Create canonical URL
  const baseUrl = "https://catsabouttowntours.com";
  const canonicalUrl = selectedPost
    ? `${baseUrl}/blog/${selectedPost.id}`
    : `${baseUrl}/blog`;

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        {selectedPost ? (
          <>
            <title>{`${selectedPost.title} | Cats About Town`}</title>
            <meta name="description" content={selectedPost.excerpt} />
            {selectedPost.tags && (
              <meta name="keywords" content={selectedPost.tags.join(', ')} />
            )}
            <link rel="canonical" href={canonicalUrl} />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": selectedPost.title,
                "description": selectedPost.excerpt,
                "author": selectedPost.author,
                "datePublished": selectedPost.date,
                "image": selectedPost.image.startsWith('http')
                  ? selectedPost.image
                  : `${baseUrl}${selectedPost.image}`,
                "mainEntityOfPage": canonicalUrl,
              })}
            </script>
          </>
        ) : (
          <>
            <title>Blog - Cats About Town Walking Tours</title>
            <meta
              name="description"
              content="Explore cat history in NYC with our blog. Latest stories about feline-focused walking tours, NYC's historic cats, and more."
            />
            <meta
              name="keywords"
              content="NYC cat blog, cat history stories, feline history NYC, cat walking tours blog"
            />
            <link rel="canonical" href={canonicalUrl} />
            {/* Breadcrumb structured data for SEO */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://catsabouttowntours.com/",
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Blog",
                    "item": "https://catsabouttowntours.com/blog",
                  },
                ],
              })}
            </script>
          </>
        )}
      </Helmet>

      {isLoading ? (
        <div className="container mx-auto px-4 py-12 text-center">
          <div role="status" aria-live="polite">
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      ) : selectedPost ? (
        <BlogDetailView
          post={selectedPost}
          onBackClick={handleBackClick}
          relatedPosts={safeBlogPosts}
        />
      ) : (
        <BlogListing
          blogPosts={safeBlogPosts}
          searchQuery={searchQuery}
          filteredPosts={filteredPosts}
          onSearchChange={handleSearchChange}
          onPostClick={handlePostClick}
          onClearSearch={handleClearSearch}
        />
      )}
    </div>
  );
};

export default Blog;
